import { useState, useRef, useEffect } from "react"
import { createQrCode, restyleQrCode, fetchRecentCode, fetchMeCodes, textFieldErrors, ApiError } from "../lib/api"
import { HONEYPOT_FIELD } from "../lib/botProtection"
import useBotProtection from "../hooks/useBotProtection"
import Turnstile from "./Turnstile"
import BuilderHero from "./BuilderHero"
import UrlForm from "./UrlForm"
import RecentCode from "./RecentCode"
import HeroResult from "./HeroResult"
import DashboardCreator from "./DashboardCreator"
import MobileBuilderSheet from "./mobile/MobileBuilderSheet"
import useMediaQuery from "../hooks/useMediaQuery"

const DEFAULT_DESIGN = {
  foregroundColor: "",
  backgroundColor: "",
  gradientColor: "",
  gradientType: "",
  frameStyle: "none",
  headerText: "",
  footerText: "",
  textWeight: "bold",
  // Server messages for refused text ({ headerText, footerText }), shown under
  // the fields. UI state like logoName: never sent, and not a restyle trigger.
  textErrors: null,
  moduleShape: "square",
  eyeShape: "square",
  logo: null,
  logoSize: "medium",
  removeLogo: false
}

const RESTYLE_DEBOUNCE_MS = 450

// The builder's design state for a code that already exists. Shared by the two
// ways a saved code is loaded into the workspace: the dashboard's edit flow and
// the hero's "Your recent code" card.
function designFromCode(code) {
  return {
    foregroundColor: code.foreground_color || "",
    backgroundColor: code.background_color || "",
    gradientColor:   code.gradient_color || "",
    gradientType:    code.gradient_type || "",
    frameStyle:      code.frame_style || "none",
    headerText:      code.header_text || "",
    footerText:      code.footer_text || "",
    textWeight:      code.text_weight || "bold",
    textErrors:      null,
    moduleShape:     code.module_shape || "square",
    eyeShape:        code.eye_shape || "square",
    logo:            null,
    // Only a code kept from this page session has one (see asRecentCode); the
    // server does not store the file name.
    logoName:        code.logo_name || null,
    logoSize:        code.logo_size || "medium",
    removeLogo:      false
  }
}

// The dashboard builder starts each new code from the look of one already made
// (#343), the newest by default, so six codes alike are styled once. These are
// the codes it offers, newest first.
const LOOK_CHOICES = 20

// The design a new code starts with when it copies `code`'s look. The logo is
// not a File: the create sends `reference_token` and the server attaches that
// code's own logo, as the preview borrows it. Shapes are Pro, so for anyone
// else they start square, which is what the server would store anyway
// (Codes::ShapeGate).
function lookDesign(code, canUseShapes) {
  const design = designFromCode(code)
  return canUseShapes ? design : { ...design, moduleShape: "square", eyeShape: "square" }
}

// A code the look picker can offer, from a create or restyle result. Only the
// rendered SVG is dropped: it is large and the picker never shows it.
function asLookCode({ svg, ...code }) {
  return code
}

// What the "Your recent code" card keeps of the code that was just on screen,
// so "Make another" shows that code and reopening it seeds the design it has
// now, not the one it had at page load. The email outcome is dropped because it
// belongs to the create that produced it (a reopened code shows no "sent"
// state, the same as after a reload), and `svg` because it is the render from
// before any restyle.
//
// What is kept of the email is `email_saved`, the one fact the server returns
// after a reload: whether an address is saved against the code. True when the
// create kept the address ("not_saved" means a ceiling refused it, so that one
// is false), when the address was saved from the mobile sheet afterwards, or
// when the server already said so. Both routes to the card then agree.
//
// `logoName` is the file name the Logo box was showing. It rides along so the
// box still names the file when the code is reopened without a reload.
function asRecentCode({ creator_email, email_sent, email_status, svg, ...code }, logoName = null) {
  return {
    ...code,
    logo_name:   code.logo_url ? logoName : null,
    email_saved: Boolean(code.email_saved) || (Boolean(creator_email) && email_status !== "not_saved")
  }
}

// The card can be hidden for this tab session. Keyed by the code's token, so a
// newer code brings the card back. Only the card is hidden: the creation
// cookie, which is the claim proof, is never touched. sessionStorage can throw
// (private mode, blocked site data), and the card must work without it.
const RECENT_HIDDEN_KEY = "tq_recent_hidden"

function readHiddenRecent() {
  try { return window.sessionStorage.getItem(RECENT_HIDDEN_KEY) } catch { return null }
}

function writeHiddenRecent(token) {
  try { window.sessionStorage.setItem(RECENT_HIDDEN_KEY, token) } catch { /* the card stays hidden for this page only */ }
}

// The vCard tool's hand-over (#332): the fields typed there, kept in
// sessionStorage (same origin, never the URL) for the builder to open as a
// hosted contact page. Read once at mount and removed after, so a reload
// starts clean. Nothing is sent until the person presses Generate.
const CONTACT_DRAFT_KEY = "tq_contact_draft"
const DRAFT_FIELDS = [ "first_name", "last_name", "business_name", "job_title", "mobile_phone", "email", "website_url" ]

function readContactDraft() {
  try {
    const raw = window.sessionStorage.getItem(CONTACT_DRAFT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object") return null
    const draft = Object.fromEntries(DRAFT_FIELDS.map((f) => [ f, typeof parsed[f] === "string" ? parsed[f] : "" ]))
    return Object.values(draft).some((v) => v.trim()) ? draft : null
  } catch {
    return null
  }
}

function clearContactDraft() {
  try { window.sessionStorage.removeItem(CONTACT_DRAFT_KEY) } catch { /* nothing to clear */ }
}

// The mobile customiser's scope, per the design handoff: below the existing
// mobile breakpoint AND a coarse pointer. Both halves matter — a narrow desktop
// window is still a mouse, and the sheet is a drag-to-resize surface built for a
// thumb. One constant so the boundary is tunable in one place if the `pointer`
// half turns out to exclude a device that wanted it.
const MOBILE_BUILDER_QUERY = "(max-width: 640px) and (pointer: coarse)"

// The live QR builder. Server-rendered marketing pages mount this into a
// <div id="qr-builder">.
//
// Funnel (deliberately URL-first): paste a URL → we create a real, working
// code and show it → you optionally customise colors / logo / frame. Step one
// is just the URL field so the widget stays small and the hero copy beside it
// stays visible. Customisation lives on the result, after the code already
// works, and applies live: tweaking a control debounced-restyles the real
// code and reloads the displayed image — no separate preview, no apply button.
//
// `destinationPlaceholder` and `caption` let a host page put its own job into
// the idle hero card: the example link in the empty field, and the line under
// the heading (#340). Unset, both keep their defaults.
//
// `contactPages` offers the hosted Contact page kind (#332): the global flag on
// the marketing mount, the user's own on the dashboard.
export default function QrBuilder({ layout = "hero", onCreated, onDone, onUpgrade, onStateChange, editingCode = null, onExitEdit, requireBotProtection = true, isPaid = false, requireEmail = false, destinationPlaceholder, caption, contactPages = false }) {
  const [status, setStatus] = useState("idle") // 'idle' | 'loading' | 'success' | 'error'
  // The vCard tool's hand-over, hero only (see readContactDraft).
  const [contactDraft, setContactDraft] = useState(() => (contactPages && layout === "hero" ? readContactDraft() : null))
  useEffect(() => { if (contactDraft) clearContactDraft() }, [contactDraft])
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  // Set when the server says an email was required but this page rendered
  // without the field — a stale cache, or a flag flipped mid-session. Reveals
  // the field inline instead of stranding the visitor on an error they can't act on.
  const [emailForced, setEmailForced] = useState(false)
  const [design, setDesign] = useState(DEFAULT_DESIGN)
  const [restyleError, setRestyleError] = useState(null)
  // Whether the design on screen is stored (#316): "idle" before any change,
  // "saving" from the moment a change is queued until the server answers, then
  // "saved", "unsaved" (refused on purpose: low contrast, refused text) or
  // "error". The result UI shows it, because a restyle has no button to say so.
  const [restyleStatus, setRestyleStatus] = useState("idle")
  // Dashboard Done pressed while a restyle was still in flight; it finishes
  // once that restyle lands.
  const [doneRequested, setDoneRequested] = useState(false)
  // Honeypot + timing stamp + Turnstile token, and the recovery all three need
  // after a refusal. Shared with EmailCapture — same server-side check, so the
  // same dead ends. See hooks/useBotProtection.
  const bot = useBotProtection()
  // Guards the live-restyle effect: ignore the run triggered by entering the
  // success state (default design — nothing to apply yet) and only react to
  // genuine user edits. Reset on "Make another" and on loading an existing code.
  const skipNextRestyle = useRef(true)
  const restyleSeq = useRef(0)
  // "Your recent code" (issue #264): the newest code this browser made, shown
  // as a card above the idle hero form so a reload doesn't read as "your code
  // is gone". `restored` marks a workspace opened from that card rather than by
  // a create a moment ago: nothing was just sent, so the result UI skips the
  // save-to-inbox prompt and the "created" pill.
  const [recentCode, setRecentCode] = useState(null)
  const [hiddenRecent, setHiddenRecent] = useState(readHiddenRecent)
  const [restored, setRestored] = useState(false)
  // "Start from the look of" (#343), dashboard only: the codes on offer and
  // the one a new code copies ("" = plain). Never "saved styles": it copies a
  // code's look once, at create, and the two codes are separate after that.
  const [lookCodes, setLookCodes] = useState([])
  const [lookToken, setLookToken] = useState("")
  // Read by the list's late answer, which must not overwrite a design the
  // person has already started on.
  const designRef = useRef(design)
  designRef.current = design
  const statusRef = useRef(status)
  statusRef.current = status
  const isPaidRef = useRef(isPaid)
  isPaidRef.current = isPaid

  // Load a code that already exists, seeding the builder's local state as if
  // we'd just finished creating it. The workspace takes over, the customize
  // panel is pre-populated, and live restyles persist back to the same code via
  // the existing effect below.
  const loadCode = (code) => {
    restyleSeq.current++ // cancel any in-flight restyle from the prior code
    skipNextRestyle.current = true
    setResult(code)
    setDesign(designFromCode(code))
    setRestyleError(null)
    setRestyleStatus("idle")
    setError(null)
    setStatus("success")
  }

  // Dashboard "edit existing code" flow: the parent passes an editingCode
  // (because the user clicked a row in YourCodesCard). Switching to a different
  // code (or back to null) re-syncs.
  useEffect(() => {
    if (!editingCode) return
    loadCode(editingCode)
  }, [editingCode?.token])

  // Ask once, after mount, and only on the marketing hero. Nothing waits on
  // this: the form is usable before it resolves and stays usable if it never
  // does (fetchRecentCode resolves null on any failure). `current || code`
  // keeps a code made on this page ahead of a slow answer about an older one.
  useEffect(() => {
    if (layout !== "hero") return
    let cancelled = false
    fetchRecentCode().then((code) => {
      if (!cancelled && code) setRecentCode((current) => current || code)
    })
    return () => { cancelled = true }
  }, [layout])

  // The dashboard's look picker: the newest codes, and the newest one's look as
  // the default. Nothing waits on this; with no answer the builder starts
  // plain, as it did before.
  useEffect(() => {
    if (layout !== "dashboard") return
    let cancelled = false
    fetchMeCodes({ per: LOOK_CHOICES, sort: "new" })
      .then(({ codes = [] }) => {
        if (cancelled || codes.length === 0) return
        // A code made before this answered is newer than any in it.
        setLookCodes((current) =>
          [ ...current, ...codes.filter((c) => !current.some((m) => m.token === c.token)) ].slice(0, LOOK_CHOICES))
        if (statusRef.current === "idle" && designRef.current === DEFAULT_DESIGN) {
          setLookToken(codes[0].token)
          setDesign(lookDesign(codes[0], isPaidRef.current))
        }
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [layout])

  const chooseLook = (token) => {
    const code = lookCodes.find((c) => c.token === token)
    setLookToken(code ? code.token : "")
    setDesign(code ? lookDesign(code, isPaid) : DEFAULT_DESIGN)
  }

  // Bring the full workspace back for the recent code. Only ever from a click
  // on the card: the visitor may be here to make a different code.
  const handleHideRecent = () => {
    if (!recentCode) return
    setHiddenRecent(recentCode.token)
    writeHiddenRecent(recentCode.token)
  }

  const handleRestore = () => {
    if (!recentCode) return
    setRestored(true)
    loadCode(recentCode)
  }

  // `contactPage` is a hosted contact page's fields (#332); with it there is
  // no destination, and the server points the code at the page.
  const handleSubmit = async (destinationUrl, email = null, label = null, contactPage = null) => {
    setStatus("loading")
    setError(null)
    try {
      // Pass the current design at create time so any pre-submit customizations
      // (dashboard mode renders CustomizePanel + LivePreview upfront) apply on
      // the first save instead of arriving via a follow-up restyle. On the
      // marketing flow the design is always default at this point, so the
      // server sees an unstyled create exactly as before.
      const created = await createQrCode({
        destinationUrl,
        // The code's name (#326); absent when the field was left empty.
        label:           label || undefined,
        foregroundColor: design.foregroundColor,
        backgroundColor: design.backgroundColor,
        gradientColor:   design.gradientColor,
        gradientType:    design.gradientType,
        frameStyle:      design.frameStyle,
        headerText:      design.headerText,
        footerText:      design.footerText,
        textWeight:      design.textWeight,
        moduleShape:     design.moduleShape,
        eyeShape:        design.eyeShape,
        logo:            design.logo,
        logoSize:        design.logoSize,
        // The look this code starts from (#343): the server attaches that
        // code's logo unless this one has its own or it was removed here.
        referenceToken:  lookToken || undefined,
        removeLogo:      Boolean(lookToken) && design.removeLogo,
        contactPage:     contactPage || undefined,
        email
      }, requireBotProtection ? bot.fields() : null)
      // The create response carries the rendered SVG + redirect (short) URL
      // but not destination_url / editable. We have both client-side (the URL
      // the user just typed; a fresh code is always editable). Merging them
      // lets the result UI treat post-create the same way as edit-existing,
      // with no special case for the destination/edit affordances.
      // A page code's destination is ours, so it comes back in the response.
      const enriched = { ...created, destination_url: contactPage ? created.destination_url : destinationUrl, editable: true }
      setResult(enriched)
      setStatus("success")
      // The hand-over is used: "Make another" opens an empty form.
      setContactDraft(null)
      // The create carried the design, so what is on screen is stored.
      setRestyleStatus("saved")
      onCreated?.(enriched)
    } catch (e) {
      const apiErr = e instanceof ApiError ? e : new ApiError("network", e.message)
      // Text set before the first save (the dashboard creator) is refused on
      // its own field, not reported as a problem with the URL.
      const textErrors = textFieldErrors(apiErr)
      if (textErrors) {
        setDesign((d) => ({ ...d, textErrors }))
        setStatus("idle")
        return
      }
      // Gate errors belong on the email field, and email_required also means
      // this page didn't render one — reveal it so the retry can succeed.
      if (apiErr.kind === "email_required") setEmailForced(true)
      // Clear every bot signal the visitor could be stuck behind — a spent
      // Turnstile token, an autofilled honeypot, a day-old timing stamp — so
      // the retry can actually pass rather than failing the same way forever.
      if (apiErr.kind === "verification_failed") bot.recover()
      setError(apiErr)
      setStatus("error")
    }
  }

  // Live restyle: when the design changes, debounced-persist it to the real
  // code and reload the displayed image (its image_url carries the new design
  // digest). Low-contrast pairs 422 — the ColorPicker already warns, so we stay
  // quiet and keep the last good image rather than nagging mid-edit.
  useEffect(() => {
    if (status !== "success" || !result) return
    if (skipNextRestyle.current) {
      skipNextRestyle.current = false
      return
    }

    setRestyleStatus("saving")
    const seq = ++restyleSeq.current
    const timeout = setTimeout(async () => {
      try {
        const updated = await restyleQrCode(result.token, design)
        if (seq !== restyleSeq.current) return
        setResult((r) => ({
          ...r,
          image_url:        updated.image_url,
          foreground_color: updated.foreground_color,
          background_color: updated.background_color,
          gradient_color:   updated.gradient_color,
          gradient_type:    updated.gradient_type,
          frame_style:      updated.frame_style,
          header_text:      updated.header_text,
          footer_text:      updated.footer_text,
          text_weight:      updated.text_weight,
          module_shape:     updated.module_shape,
          eye_shape:        updated.eye_shape,
          logo_size:        updated.logo_size,
          // The saved logo, or null once it is removed. The File is dropped
          // just below, so this URL is all the Logo box has left to show:
          // without it the box read "no logo" right after an upload, and a
          // removed logo came back from the stale URL. Issue #266.
          logo_url:         updated.logo_url,
          design_digest:    updated.design_digest
        }))
        onCreated?.({ ...result, ...updated })
        setRestyleError(null)
        // The logo (and any pending removal) is now persisted; drop the File so
        // a later color tweak doesn't re-upload it, and keep its name for the
        // Logo box. No-op when nothing changed, so this doesn't re-trigger the
        // effect on its own.
        // On a code nobody owns, only the browser that made it may set text
        // (Api::V1::QrCodesController#restyle_params); anyone else's is dropped
        // and the response carries what was saved. Say so, or the field would
        // show text the file doesn't have.
        const dropped = "Only the browser that made this code can add text to it."
        const textErrors =
          (design.headerText || "").trim() && !updated.header_text ? { headerText: dropped, footerText: null } :
          (design.footerText || "").trim() && !updated.footer_text ? { headerText: null, footerText: dropped } :
          null
        // Dropped text was not stored, so the file does not match the screen.
        setRestyleStatus(textErrors ? "unsaved" : "saved")
        setDesign((d) => {
          const next = d.logo || d.removeLogo ? { ...d, logo: null, logoName: d.logo?.name || null, removeLogo: false } : d
          if (!textErrors && !next.textErrors) return next
          return { ...next, textErrors }
        })
      } catch (e) {
        if (seq !== restyleSeq.current) return
        // Refused text gets its message under the field: unlike a color pair,
        // nothing on the page has warned about it yet.
        const textErrors = textFieldErrors(e)
        if (textErrors) {
          setDesign((d) => ({ ...d, textErrors }))
          setRestyleStatus("unsaved")
          return
        }
        if (e instanceof ApiError && e.kind === "validation") {
          setRestyleStatus("unsaved")
          return
        }
        setRestyleError(e instanceof ApiError ? e.message : "Couldn't update your code.")
        setRestyleStatus("error")
      }
    }, RESTYLE_DEBOUNCE_MS)

    return () => clearTimeout(timeout)
  }, [design.foregroundColor, design.backgroundColor, design.gradientColor, design.gradientType, design.frameStyle, design.headerText, design.footerText, design.textWeight, design.moduleShape, design.eyeShape, design.logo, design.logoSize, design.removeLogo, status, result?.token])

  const handleReset = () => {
    restyleSeq.current++ // cancel any in-flight restyle
    skipNextRestyle.current = true
    // The code leaving the screen becomes the recent one, as it stands now. The
    // idle form then shows the card for it straight away, the same card a
    // reload would show, without a second request.
    if (layout === "hero" && result) setRecentCode(asRecentCode(result, design.logoName))
    setRestored(false)
    setStatus("idle")
    setResult(null)
    setError(null)
    if (layout === "dashboard" && result?.token && !editingCode) {
      // The code just made is now the newest one, so the next code starts from
      // its look as it stands after any restyle (#343).
      const made = asLookCode(result)
      setLookCodes((codes) => [ made, ...codes.filter((c) => c.token !== made.token) ].slice(0, LOOK_CHOICES))
      setLookToken(made.token)
      setDesign(lookDesign(made, isPaid))
    } else {
      setLookToken("")
      setDesign(DEFAULT_DESIGN)
    }
    setRestyleError(null)
    setRestyleStatus("idle")
    setDoneRequested(false)
    // If we got into this success state by loading an existing code (edit
    // flow), tell the parent so it clears its editingCode — otherwise the
    // editingCode useEffect above would immediately re-seed the same code.
    onExitEdit?.()
  }

  // Dashboard Done (#316): hand the finished code to the list, then clear the
  // builder for the next one. A reset cancels a restyle still waiting out its
  // debounce, so while one is in flight Done waits for it instead of dropping
  // the change. If that restyle fails, the builder stays open to say so.
  const finish = () => {
    const code = result
    handleReset()
    onDone?.(code)
  }
  const handleDone = () => {
    if (restyleStatus === "saving") setDoneRequested(true)
    else finish()
  }
  useEffect(() => {
    if (!doneRequested || restyleStatus === "saving") return
    setDoneRequested(false)
    if (restyleStatus === "saved") finish()
  }, [doneRequested, restyleStatus])

  const isLoading = status === "loading"
  const showResult = status === "success" && result
  // Mobile customiser (design handoff, Direction B). Only the LIVE state forks:
  // idle keeps one code path across every viewport, because the difference there
  // is spacing, not structure.
  //
  // There is deliberately no way to dismiss the shell back to the hero
  // workspace. It briefly had one, and it was a regression: "closing" the
  // mobile experience dropped the visitor onto the stacked phone layout the
  // shell exists to replace. On a phone this IS the hero — the ways out are
  // collapsing the drawer or starting a new code, both of which stay inside it.
  const isMobile = useMediaQuery(MOBILE_BUILDER_QUERY)
  const formError = status === "error" ? error?.message : null
  // Gate rejections route to the email input; a bot-layer refusal routes to the
  // form itself (nothing is wrong with either field, so reddening one of them
  // sends the visitor off to fix something that isn't broken); everything else
  // stays on the URL field. UrlForm accepts a bare string too, so other callers
  // are unaffected.
  const emailGateError = error?.kind === "email_required" || error?.kind === "email_invalid"
  const verificationError = error?.kind === "verification_failed"
  // A destination-integrity refusal belongs on the URL field, with its re-check link.
  const integrityError = status === "error" && error?.kind === "destination_unsafe"
    ? { field: "url", message: error.message, appealUrl: error.appealUrl }
    : null
  // A refused contact page (#332) goes to the page's own fields.
  const contactPageError = status === "error" && error?.field === "contact_page"
    ? { field: "contact_page", message: error.message, details: error.details }
    : null
  const formErrorForUrlForm = status !== "error"
    ? formError
    : emailGateError
      ? { field: "email", message: error.message }
      : verificationError
        ? { field: "form", message: error.message }
        : contactPageError || integrityError || formError
  const showEmailField = requireEmail || emailForced

  // Broadcast idle/live to the host page so the marketing hero grid can reflow
  // (copy beside the builder when idle; beneath it once a code is live). Fires
  // "idle" on mount/reset and "live" on a successful create. The dashboard
  // mount passes no handler, so this is a no-op there.
  const isLive = Boolean(showResult)
  useEffect(() => {
    onStateChange?.(isLive ? "live" : "idle")
  }, [isLive, onStateChange])

  // Honeypot + Turnstile go in both pre-submit branches — bot defense is per
  // action on the create endpoint. Extracted so the markup doesn't fork twice.
  //
  // `requireBotProtection={false}` opts the field set out entirely. The
  // server already skips honeypot / timing / Turnstile validation for
  // signed-in callers (see `anonymous?` in Api::V1::QrCodesController), so
  // for in-dashboard usage there's no protection to lose — but rendering
  // <Turnstile> still fetches Cloudflare and shows a challenge box on the
  // page for no functional reason, which is the visible noise the
  // dashboard builder shouldn't carry.
  const botProtectionFields = requireBotProtection ? (
    <>
      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
        <label htmlFor="qr-builder-company">Company (leave this blank)</label>
        <input
          id="qr-builder-company"
          name={HONEYPOT_FIELD}
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={bot.honeypot}
          onChange={(e) => bot.setHoneypot(e.target.value)}
        />
      </div>
      <Turnstile {...bot.turnstileProps} />
    </>
  ) : null

  // Dashboard: the redesigned two-pane workspace owns all four states (empty,
  // generating, created, editing) in one persistent layout — no ResultCard swap.
  // The parent (Dashboard.jsx) already wraps the builder in a card, so the
  // workspace renders bare. All state + effects above stay here; DashboardCreator
  // is presentation.
  if (layout === "dashboard") {
    return (
      <div className="w-full">
        <DashboardCreator
          status={status}
          result={result}
          formError={contactPageError || integrityError || formError}
          contactPages={contactPages}
          design={design}
          setDesign={setDesign}
          restyleError={restyleError}
          restyleStatus={restyleStatus}
          doneRequested={doneRequested}
          botProtectionFields={botProtectionFields}
          onSubmit={handleSubmit}
          onReset={handleReset}
          onDone={handleDone}
          // Destination edits re-publish through the same onCreated channel so
          // YourCodesCard's optimistic row-update picks up the new destination
          // without a refetch; also patch local result so the rail stays in sync.
          onUpdated={(updated) => {
            setResult((r) => ({ ...r, ...updated }))
            onCreated?.(updated)
          }}
          isPaid={isPaid}
          onUpgrade={onUpgrade}
          isEditing={!!editingCode}
          look={{ codes: lookCodes, token: lookToken, onChange: chooseLook }}
        />
      </div>
    )
  }

  // Hero (marketing landing): a compact, URL-first idle card that, on success,
  // claims the full hero width and becomes the two-pane workspace (HeroResult).
  // The host page reflows the marketing copy around it via onStateChange.
  const heroWrapperClass = showResult ? "w-full" : "w-full max-w-[520px] mx-auto lg:mx-0"
  // The full-screen mobile shell replaces the hero workspace entirely while it
  // is up, so it renders outside the hero wrapper — that wrapper's width and
  // card chrome mean nothing to a fixed overlay.
  if (showResult && layout === "hero" && isMobile) {
    return (
      <MobileBuilderSheet
        result={result}
        design={design}
        setDesign={setDesign}
        restyleError={restyleError}
        onReset={handleReset}
        onUpgrade={onUpgrade}
        isPaid={isPaid}
        creatorEmail={result?.creator_email || null}
        emailStatus={result?.email_status}
        restored={restored}
        // An address saved from the sheet is recorded on the result, so the
        // recent card carries it (see asRecentCode).
        onEmailSaved={() => setResult((r) => (r ? { ...r, email_saved: true } : r))}
      />
    )
  }

  return (
    <div className={heroWrapperClass}>
      {/* Above the idle form, never over a live result. Held while a create is
          in flight, so its answer can't land on a workspace the card opened. */}
      {!showResult && recentCode && recentCode.token !== hiddenRecent && (
        <RecentCode code={recentCode} onOpen={handleRestore} onHide={handleHideRecent} disabled={isLoading} />
      )}
      {showResult ? (
        <HeroResult
          result={result}
          design={design}
          setDesign={setDesign}
          restyleError={restyleError}
          restyleStatus={restyleStatus}
          onReset={handleReset}
          onUpgrade={onUpgrade}
          isPaid={isPaid}
          creatorEmail={result?.creator_email || null}
          emailSent={result?.email_sent}
          emailStatus={result?.email_status}
          restored={restored}
        />
      ) : (
        // Full-bleed on a phone: a card inside a 16px page gutter wastes ~40px
        // of a 390px screen on chrome the content doesn't need. The card
        // returns at `sm:`, where it has room to read as a card.
        <div className="bg-transparent border-0 shadow-none rounded-none p-0 sm:bg-white sm:dark:bg-midnight sm:rounded-2xl sm:shadow-lg sm:border sm:border-[color:var(--color-border)] sm:dark:border-storm sm:p-8">
          <BuilderHero align="left" caption={caption} />
          {/* A press made before the browser check has a token is held and
              sent once it does (#339), so it never goes out with an empty one. */}
          <UrlForm
            onSubmit={(url, email, label, page) => bot.submitWhenReady(() => handleSubmit(url, email, label, page))}
            disabled={isLoading}
            verifying={bot.turnstilePending}
            held={bot.held}
            serverError={formErrorForUrlForm}
            requireEmail={showEmailField}
            destinationPlaceholder={destinationPlaceholder}
            contactPages={contactPages}
            initialPage={contactDraft}
          />
          {/* Two concrete reassurances, in place of the single padlock line, on
              the viewport where the hero copy beside the builder is pushed off
              screen and this is the only pitch a visitor sees. The gated arm
              still has to say an email is required up front — promising "no
              signup" beside a mandatory email field is the bait-and-switch the
              email-gate spec exists to avoid. */}
          <ul className="mt-5 flex list-none flex-col gap-3 p-0 sm:hidden">
            {[
              showEmailField
                ? "Your email, then your code — no account, no card"
                : "No signup for your first code",
              "Style it after you generate — the link never changes"
            ].map((line) => (
              <li key={line} className="flex items-start gap-2.5 text-sm leading-snug text-coal dark:text-cloud">
                <span aria-hidden="true" className="mt-px inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-voltage-pale text-voltage-deep dark:bg-voltage/15 dark:text-voltage-light">
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                </span>
                {line}
              </li>
            ))}
          </ul>
          <p className="mt-4 hidden items-center gap-2 text-xs text-[color:var(--color-muted)] dark:text-muted-light sm:flex">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0 text-voltage-deep dark:text-voltage-light">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            {/* Says what happens on submit. The gated arm asks for an address
                before showing anything, so a visitor can't otherwise tell
                whether clicking generates the code here, emails it, or both.
                It's both. Issue #166. */}
            {showEmailField
              ? "No account, no card. Your code appears right here, and we'll email you a copy. Customize the colors, frame, and logo after you generate."
              : "No signup. Your code appears right here. Customize the colors, frame, and logo after you generate."}
          </p>
          {botProtectionFields}
        </div>
      )}
    </div>
  )
}
